import { Button } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useEffect, useRef, useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import { useDeleteUserNode } from '/@/hooks/nodes';
import { useValidatedType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { GrowerCreationEntrySource, GrowerCreationExitAction } from '/@/store/tracking/events/growers';
import AddGrowerSection from './add-grower-section';
import {
    endGrowerCreationFlow,
    type GrowerCreationFlow,
    GrowerCreationFlowContext,
    startGrowerCreationFlow,
} from './flow-tracking';
import NewGrowersModalSidebar from './sidebar';
import { SingleGrowerFormProvider, useSingleGrowerFormApi } from './single-grower-form-context';

export type SidebarGrower = {
    id?: number;
    name?: string;
    /** Present when tenant uses CUIT as grower identifier. */
    cuit?: string;
    segment?: string;
    advisor?: string;
    rv?: string;
    location?: string;
    /** Present when tenant uses email as grower identifier (also used as contact email). */
    email?: string;
    phone?: string;
    editable: boolean;
};

function NewGrowersModalContent({
    open,
    setOpen,
    onAddFields,
    singleGrower,
    onGrowerAdded,
    defaultRv,
    entrySource,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    onAddFields: (growerUserNodeId?: number) => void;
    singleGrower: boolean;
    onGrowerAdded?: (growerUserNodeId?: number) => void;
    defaultRv?: string;
    entrySource: GrowerCreationEntrySource;
}) {
    const [growers, setGrowers] = useState<SidebarGrower[]>([]);
    const [editingGrowerIndex, setEditingGrowerIndex] = useState<number | null>(null);
    const deleteUserNodeMutation = useDeleteUserNode();
    const singleGrowerFormApi = useSingleGrowerFormApi();
    const userProfile = useValidatedType();
    const flowRef = useRef<GrowerCreationFlow | null>(null);
    /** How the modal is being left, set by the button that closes it; null for X, backdrop and Esc. */
    const exitActionRef = useRef<GrowerCreationExitAction | null>(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: a pass starts and ends with `open`, not with the props it records
    useEffect(() => {
        if (open) {
            setGrowers([]);
            setEditingGrowerIndex(null);
            exitActionRef.current = null;
            flowRef.current = startGrowerCreationFlow(entrySource, singleGrower, userProfile);
            return;
        }
        endGrowerCreationFlow(flowRef.current, exitActionRef.current ?? 'close');
    }, [open]);

    // Leaving the page with the modal open (e.g. "Ver productor") never sets `open` to false. A button
    // that closes the modal and navigates in the same click still gets its own exit action.
    useEffect(() => () => endGrowerCreationFlow(flowRef.current, exitActionRef.current ?? 'left_page'), []);

    const close = (exitAction: GrowerCreationExitAction) => {
        exitActionRef.current = exitAction;
        setOpen(false);
    };

    const handleAddGrower = (grower: SidebarGrower) => {
        setGrowers((prev) => [...prev, grower]);
        setEditingGrowerIndex(null);
    };

    const handleUpdateGrower = (index: number, grower: SidebarGrower) => {
        setGrowers((prev) => {
            const next = [...prev];
            next[index] = grower;
            return next;
        });
        setEditingGrowerIndex(null);
    };

    const handleDeleteGrower = async (index: number) => {
        const grower = growers[index];
        if (grower?.id !== undefined) {
            await deleteUserNodeMutation.trigger(grower.id);
        }
        setGrowers((prev) => prev.filter((_, i) => i !== index));
        setEditingGrowerIndex(null);
    };

    const handleSaveAndAddFields = async () => {
        if (!singleGrowerFormApi) return;
        const { success, growerId } = await singleGrowerFormApi.submit();
        if (success) {
            close('save_and_add_fields');
            onAddFields(growerId);
            onGrowerAdded?.(growerId);
        }
    };

    return (
        <GrowerCreationFlowContext.Provider value={flowRef}>
            <ModalV2
                open={open}
                onClose={() => setOpen(false)}
                title={singleGrower ? _('Agregar productor') : _('Agregar productores')}
                maxWidth='md'
                bodyProps={{
                    alignItems: 'stretch',
                    textAlign: 'left',
                    width: '100%',
                    spacing: 0,
                }}
                actions={
                    !singleGrower ? (
                        <>
                            <Button variant='text' color='secondary' onClick={() => close('finish')}>
                                {_('Finalizar')}
                            </Button>
                            <Button
                                variant='contained'
                                color='secondary'
                                onClick={() => {
                                    // The modal stays open behind the fields modal; the pass ends here anyway.
                                    endGrowerCreationFlow(flowRef.current, 'add_fields');
                                    onAddFields();
                                }}
                                disabled={growers.length === 0}
                            >
                                {_('Agregar los lotes por productor')}
                            </Button>
                        </>
                    ) : (
                        <Button
                            type='button'
                            variant='contained'
                            color='secondary'
                            onClick={handleSaveAndAddFields}
                            disabled={!singleGrowerFormApi?.isValid || singleGrowerFormApi?.isSubmitting}
                            loading={singleGrowerFormApi?.isSubmitting}
                        >
                            {_('Guardar y agregar lotes')}
                        </Button>
                    )
                }
                actionsProps={{ sx: { justifyContent: 'flex-end' } }}
            >
                <Grid2 container spacing={2} width='100%'>
                    <Grid2 xs={12} md={singleGrower ? 12 : 8} sx={{ overflow: 'auto' }}>
                        <AddGrowerSection
                            singleGrower={singleGrower}
                            growers={growers}
                            editingGrowerIndex={editingGrowerIndex}
                            onAddGrower={handleAddGrower}
                            onUpdateGrower={handleUpdateGrower}
                            defaultRv={defaultRv}
                        />
                    </Grid2>
                    {!singleGrower && (
                        <Grid2 xs={12} md sx={{ overflow: 'auto' }}>
                            <NewGrowersModalSidebar
                                growers={growers}
                                onEdit={(index) => setEditingGrowerIndex(index)}
                                onDelete={handleDeleteGrower}
                            />
                        </Grid2>
                    )}
                </Grid2>
            </ModalV2>
        </GrowerCreationFlowContext.Provider>
    );
}

const NewGrowersModalV2 = ({
    open,
    setOpen,
    onAddFields,
    singleGrower = false,
    onGrowerAdded,
    defaultRv,
    entrySource,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    onAddFields: (growerUserNodeId?: number) => void;
    singleGrower?: boolean;
    onGrowerAdded?: (growerUserNodeId?: number) => void;
    defaultRv?: string;
    /** Reported on `Grower Creation flow:Started`. */
    entrySource: GrowerCreationEntrySource;
}) => (
    <SingleGrowerFormProvider>
        <NewGrowersModalContent
            open={open}
            setOpen={setOpen}
            onAddFields={onAddFields}
            singleGrower={singleGrower}
            onGrowerAdded={onGrowerAdded}
            defaultRv={defaultRv}
            entrySource={entrySource}
        />
    </SingleGrowerFormProvider>
);

export default NewGrowersModalV2;
